import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useListChecklistRunsQuery } from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { ChecklistRunDetails } from '../../components/checklists/ChecklistRunDetails';
import { ChecklistKind, ChecklistRunStatus, ClientChecklistRun } from '../../types';
import { dublinDateString, formatCivilDate } from '../../utils/date';

const KIND_LABELS: Record<ChecklistKind, string> = {
  [ChecklistKind.OPENING]: 'Opening',
  [ChecklistKind.CLOSING]: 'Closing',
};

const STATUS_LABELS: Record<ChecklistRunStatus, { label: string; className: string }> = {
  NOT_STARTED: { label: 'Not started', className: 'bg-gray-100 text-gray-600' },
  IN_PROGRESS: { label: 'In progress', className: 'bg-gray-100 text-gray-600' },
  COMPLETE: { label: 'Complete', className: 'bg-emerald-50 text-emerald-700' },
  SIGNED_OFF: { label: 'Signed off', className: 'bg-primary-50 text-primary-700' },
};

/** First and last Dublin calendar dates of a YYYY-MM month. */
function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-IE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Every checklist run at this location, month by month, newest first, each
 * expandable to its answers, note and who did / signed it. Staff and admins
 * alike: "did we do closing yesterday" should not need a manager. Read-only.
 * Not a statutory register: no Tusla/HSA claim, no alarm styling.
 */
const ChecklistHistoryPage: React.FC = () => {
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;
  const { isStaffOrAbove } = useAuth();

  const [month, setMonth] = useState(() => dublinDateString().slice(0, 7));
  const currentMonth = useMemo(() => dublinDateString().slice(0, 7), []);
  const { from, to } = monthBounds(month);

  const { data, isLoading, isError, refetch } = useListChecklistRunsQuery(
    { locationId: locationId ?? '', from, to },
    { skip: !locationId || !isStaffOrAbove }
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (!isStaffOrAbove) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        You don&apos;t have access to this page.
      </div>
    );
  }
  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  // Newest date first; within a date, Opening before Closing.
  const byDate = new Map<string, ClientChecklistRun[]>();
  for (const run of data?.runs ?? []) {
    byDate.set(run.date, [...(byDate.get(run.date) ?? []), run]);
  }
  const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : -1));
  const total = data?.runs.length ?? 0;

  return (
    <div data-testid="checklist-history" className="space-y-6 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            data-testid="checklist-history-month-prev"
            aria-label="Previous month"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            className="h-8 w-8 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
          >
            ‹
          </button>
          <span
            data-testid="checklist-history-month"
            className="min-w-[140px] text-center text-sm font-semibold text-gray-800"
          >
            {monthLabel(month)}
          </span>
          <button
            type="button"
            data-testid="checklist-history-month-next"
            aria-label="Next month"
            disabled={month >= currentMonth}
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            className="h-8 w-8 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ›
          </button>
        </div>
        {!isLoading && !isError && (
          <span className="text-xs text-gray-500">
            {total === 0 ? 'No checklists recorded' : `${total} checklist${total === 1 ? '' : 's'}`}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-slate-500">
          <p className="text-base font-medium text-slate-700">Failed to load history.</p>
          <button
            type="button"
            data-testid="checklist-history-retry"
            onClick={() => refetch()}
            className="text-primary-600 hover:text-primary-500 underline"
          >
            Try again
          </button>
        </div>
      ) : dates.length === 0 ? (
        <p data-testid="checklist-history-empty" className="py-8 text-center text-sm text-gray-500">
          No checklists recorded in {monthLabel(month)}.
        </p>
      ) : (
        <div className="space-y-5">
          {dates.map((date) => (
            <div key={date} className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {formatCivilDate(date)}
              </h3>
              {byDate
                .get(date)!
                .slice()
                .sort((a, b) =>
                  a.kind === ChecklistKind.OPENING ? -1 : b.kind === ChecklistKind.OPENING ? 1 : 0
                )
                .map((run) => {
                  const isExpanded = expanded.has(run.runId);
                  const status = STATUS_LABELS[run.status];
                  return (
                    <div
                      key={run.runId}
                      data-testid={`checklist-history-run-${run.runId}`}
                      className="rounded-lg border border-gray-200 bg-white shadow-sm"
                    >
                      <button
                        type="button"
                        data-testid={`checklist-history-run-toggle-${run.runId}`}
                        aria-expanded={isExpanded}
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(run.runId)) next.delete(run.runId);
                            else next.add(run.runId);
                            return next;
                          })
                        }
                        className="flex w-full items-center gap-3 px-4 py-3 text-left"
                      >
                        <span className="flex flex-1 flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {KIND_LABELS[run.kind]}
                          </span>
                          <span
                            data-testid={`checklist-history-status-${run.runId}`}
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.className}`}
                          >
                            {status.label}
                          </span>
                          {run.note && (
                            <span
                              data-testid={`checklist-history-note-badge-${run.runId}`}
                              className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                            >
                              Note
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {run.startedByName}
                          {run.signedOffByName ? ` · signed off by ${run.signedOffByName}` : ''}
                        </span>
                        <span className="text-xs text-primary-600">
                          {isExpanded ? 'Hide' : 'Show'}
                        </span>
                      </button>
                      {isExpanded && <ChecklistRunDetails run={run} />}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChecklistHistoryPage;
